// AtomicAssets explorer endpoints (fallback). The claim calculator relies on these.
// You can override at build time via REACT_APP_ATOMIC_API_BASE (either root host or full /atomicassets/v1 path).
const ENV_ATOMIC_BASE = (() => {
  try {
    // CRA injects process.env at build time.
    // eslint-disable-next-line no-undef
    const v = typeof process !== "undefined" && process?.env ? process.env.REACT_APP_ATOMIC_API_BASE : "";
    return String(v || "").trim();
  } catch {
    return "";
  }
})();

function normalizeAtomicBase(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  // accept either full base or host root
  if (v.includes("/atomicassets/v1")) return v.replace(/\/+$/, "");
  return `${v.replace(/\/+$/, "")}/atomicassets/v1`;
}

export const ATOMIC_API_BASES = [
  normalizeAtomicBase(ENV_ATOMIC_BASE),
  // Bloxprod AtomicAssets API (mainnet)
  "https://xpr-mainnet-atm-api.bloxprod.io/atomicassets/v1",
  "https://proton.api.atomicassets.io/atomicassets/v1",
  // Community/public fallback endpoints (may be rate-limited).
  "https://aa-xprnetwork-main.saltant.io/atomicassets/v1",
  "https://proton.pink.gg/atomicassets/v1",
].filter(Boolean);

export function parseImmutableRequirements(reqArr) {
  const arr = Array.isArray(reqArr) ? reqArr : [];
  const out = [];
  for (const s of arr) {
    const t = String(s || "").trim();
    if (!t) continue;
    const idx = t.indexOf("=");
    if (idx <= 0 || idx >= t.length - 1) continue;
    const key = t.slice(0, idx).trim();
    const value = t.slice(idx + 1).trim();
    if (!key || !value) continue;
    out.push({ key, value });
  }
  return out;
}

export function mergeImmutableData(asset) {
  const out = {...(asset?.template?.immutable_data || {}),...(asset?.immutable_data || {}) };
  return out;
}

export function matchesRequirements(asset, reqPairs) {
  if (!reqPairs || reqPairs.length === 0) return true;
  const data = mergeImmutableData(asset);
  for (const { key, value } of reqPairs) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) return false;
    if (String(data[key]) !== String(value)) return false;
  }
  return true;
}

/** Parse requiredMutable entries: "key" (presence) or "key=value" (exact, asset mutable_data only). */
export function parseMutableRequirements(reqArr) {
  const arr = Array.isArray(reqArr) ? reqArr : [];
  const out = [];
  for (const s of arr) {
    const t = String(s || "").trim();
    if (!t) continue;
    const idx = t.indexOf("=");
    if (idx < 0) {
      const key = t.trim();
      if (key) out.push({ key, value: null, presenceOnly: true });
    } else {
      const key = t.slice(0, idx).trim();
      const value = t.slice(idx + 1).trim();
      if (!key) continue;
      if (value.length === 0) continue;
      out.push({ key, value, presenceOnly: false });
    }
  }
  return out;
}

export function getMutableData(asset) {
  const md = asset?.mutable_data;
  if (md && typeof md === "object" && !Array.isArray(md)) return { ...md };
  return {};
}

function mutableFieldIsSet(data, key) {
  if (!Object.prototype.hasOwnProperty.call(data, key)) return false;
  const v = data[key];
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.length > 0;
  if (typeof v === "number" || typeof v === "boolean") return true;
  return true;
}

/** Match AtomicAssets API asset against requiredMutable (values from mutable_data only). */
export function matchesMutableRequirements(asset, reqArrOrPairs) {
  const pairs =
    Array.isArray(reqArrOrPairs) &&
    reqArrOrPairs.length > 0 &&
    typeof reqArrOrPairs[0] === "object" &&
    reqArrOrPairs[0] != null &&
    "key" in reqArrOrPairs[0]
      ? reqArrOrPairs
      : parseMutableRequirements(reqArrOrPairs);
  if (!pairs.length) return true;
  const data = getMutableData(asset);
  for (const { key, value, presenceOnly } of pairs) {
    if (presenceOnly) {
      if (!mutableFieldIsSet(data, key)) return false;
    } else {
      if (!Object.prototype.hasOwnProperty.call(data, key)) return false;
      if (String(data[key]) !== String(value)) return false;
    }
  }
  return true;
}

/** Pool claim NFT: immutable (template + asset) and optional mutable (asset only). */
export function matchesPoolAssetRequirements(asset, immutableReqArr, mutableReqArr) {
  const immPairs = parseImmutableRequirements(immutableReqArr);
  if (!matchesRequirements(asset, immPairs)) return false;
  const mutArr = Array.isArray(mutableReqArr) ? mutableReqArr : [];
  if (!mutArr.length) return true;
  return matchesMutableRequirements(asset, mutArr);
}

export async function fetchAtomicJson(path, params) {
  const p = params || {};
  let lastErr = null;
  const attempted = [];

  for (const base of ATOMIC_API_BASES) {
    attempted.push(base);
    try {
      const u = new URL(`${base}${path}`);
      Object.keys(p).forEach((k) => {
        const v = p[k];
        if (v === undefined || v === null || String(v) === "") return;
        u.searchParams.set(k, String(v));
      });

      // Use a short-ish timeout to avoid hanging forever.
      const controller = new AbortController();
      const to = setTimeout(() => controller.abort(), 20000);
      let res;
      try {
        res = await fetch(u.toString(), { signal: controller.signal });
      } finally {
        clearTimeout(to);
      }

      if (!res.ok) {
        // Try another endpoint for transient/server issues
        const status = res.status;
        const e = new Error(`Atomic API error (${status})`);
        // @ts-ignore
        e.__atomicStatus = status;
        throw e;
      }
      const json = await res.json();
      if (json && json.success === false) throw new Error(json.message || "Atomic API error");
      return json;
    } catch (e) {
      lastErr = e;
      const msg = String(e?.message || e || "");
      const status = e?.__atomicStatus;
      const retryable =
        msg.includes("NetworkError") ||
        msg.includes("Failed to fetch") ||
        msg.includes("aborted") ||
        msg.includes("AbortError") ||
        status === 429 ||
        status === 502 ||
        status === 503 ||
        status === 504;
      if (!retryable) break;
    }
  }

  const lastMsg = String(lastErr?.message || lastErr || "").trim();
  const msg = `AtomicAssets API request failed. Tried: ${attempted.join(", ")}. ${lastMsg ? `Last error: ${lastMsg}` : ""}`;
  throw new Error(msg);
}

export async function fetchAllAtomicAssets({ collectionName, owner, limit = 1000, maxPages = 200 }) {
  const out = [];
  for (let page = 1; page <= maxPages; page++) {
    const json = await fetchAtomicJson("/assets", {
      collection_name: collectionName,
      owner,
      page,
      limit,
      order: "desc",
      sort: "asset_id",
      burned: "false",
    });
    const rows = json?.data || [];
    out.push(...rows);
    if (!rows || rows.length < limit) break;
  }
  return out;
}

export async function atomicCollectionExists(collectionName) {
  const c = String(collectionName || "").trim();
  if (!c) return { ok: true, exists: false };

  try {
    const json = await fetchAtomicJson(`/collections/${encodeURIComponent(c)}`);
    const name =
      json?.data?.collection_name ||
      json?.data?.collection?.collection_name ||
      json?.data?.name ||
      "";
    const exists = !!name;
    return { ok: true, exists };
  } catch (e) {
    const msg = String(e?.message || e || "");
    // If Atomic API returns 404 for missing collection, treat as "not found" (ok: true, exists: false).
    if (msg.includes("(404)")) return { ok: true, exists: false };
    return { ok: false, exists: false, error: msg };
  }
}

export async function mapLimit(items, limit, fn) {
  const arr = Array.isArray(items) ? items : [];
  const out = new Array(arr.length);
  let idx = 0;
  const workers = new Array(Math.max(1, limit)).fill(0).map(async () => {
    while (idx < arr.length) {
      const cur = idx++;
      out[cur] = await fn(arr[cur], cur);
    }
  });
  await Promise.all(workers);
  return out;
}
